import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  MapPin, Search, Star, Verified, Briefcase, Calendar, 
  Filter, SortDesc, Users, Clock, MapPinned, Navigation,
  QrCode, ExternalLink, MessageCircle, CheckCircle, 
  GraduationCap, Heart, Shield, Wrench, Home, Plane, Store
} from 'lucide-react';
import BusinessCard from './SimpleBusinessCard';

const LocationFirstSearch = ({ onResults, onLoading }) => {
  const [location, setLocation] = useState('');
  const [activeTab, setActiveTab] = useState('directory');
  const [searchResults, setSearchResults] = useState({
    featured: [],
    verified: [],
    directory: [],
    jobs: [],
    events: []
  });
  const [filters, setFilters] = useState({
    discountTypes: [],
    sortBy: 'relevance',
    verifiedOnly: false
  });
  const [loading, setLoading] = useState(false);
  const [locationSet, setLocationSet] = useState(false);
  const [geoLocation, setGeoLocation] = useState(null);

  // Customer segment offers
  const liveOffers = [
    { icon: GraduationCap, label: 'Students', count: 'up to 15% off', color: 'bg-blue-500' },
    { icon: Heart, label: 'Pensioners', count: 'up to 20% off', color: 'bg-pink-500' },
    { icon: Shield, label: 'Government', count: 'up to 10% off', color: 'bg-green-500' },
    { icon: Wrench, label: 'Trade', count: 'up to 12% off', color: 'bg-orange-500' },
    { icon: Home, label: 'Residents', count: 'up to 8% off', color: 'bg-purple-500' },
    { icon: Plane, label: 'Visitors', count: 'up to 6% off', color: 'bg-indigo-500' }
  ];

  const discountTypeFilters = [
    '2-for-1', 'Student Discount', 'NHS Discount', 'Senior Discount', 
    'Flash Sales', 'Bulk Discount', 'Loyalty Rewards', 'First-time Customer'
  ];

  const handleLocationSearch = async () => {
    if (!location.trim()) return;
    
    setLoading(true);
    onLoading(true);
    
    try {
      // Simulate API call for location-based search
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock results based on location
      const mockResults = {
        featured: [
          {
            id: 'f1',
            name: 'Tesco Express Premium',
            logo: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=100',
            address: '123 High Street, Birmingham B1 2AA',
            tier: 'platinum',
            isVerified: true,
            chatbotUrl: 'tesco-express.shoppersdiscount.club',
            offers: ['10% off weekly shop over £50'],
            category: 'supermarkets'
          },
          {
            id: 'f2', 
            name: 'Marks & Spencer Elite',
            logo: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100',
            address: '456 Market Street, Birmingham B2 4DX',
            tier: 'elite',
            isVerified: true,
            chatbotUrl: 'marks-spencer.shoppersdiscount.club',
            offers: ['Buy 2 Get 1 Free on selected items'],
            category: 'retail'
          }
        ],
        verified: [
          {
            id: 'v1',
            name: 'Sainsbury\'s Local',
            logo: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=100',
            address: '789 City Centre, Birmingham B3 1LM',
            tier: 'deluxe',
            isVerified: true,
            chatbotUrl: 'sainsburys-local.shoppersdiscount.club',
            offers: ['5% off first order'],
            category: 'supermarkets'
          }
        ],
        jobs: [
          {
            id: 'j1',
            title: 'Retail Assistant - Tesco',
            company: 'Tesco Express',
            location: 'Birmingham',
            type: 'Part-time',
            salary: '£9.50/hour',
            posted: '2 days ago'
          },
          {
            id: 'j2',
            title: 'Store Manager - M&S',
            company: 'Marks & Spencer',
            location: 'Birmingham',
            type: 'Full-time',
            salary: '£35,000/year',
            posted: '1 week ago'
          }
        ],
        events: [
          {
            id: 'e1',
            title: 'Birmingham Farmers Market',
            location: 'Bullring Square',
            date: '2025-01-28',
            time: '9:00 AM - 4:00 PM',
            type: 'Market'
          },
          {
            id: 'e2',
            title: 'Winter Sale Weekend',
            location: 'Grand Central Shopping',
            date: '2025-01-30',
            time: '10:00 AM - 8:00 PM',
            type: 'Sale Event'
          }
        ]
      };
      
      setSearchResults(mockResults);
      setLocationSet(true);
      onResults(mockResults);
      
    } catch (error) {
      console.error('Location search error:', error);
    } finally {
      setLoading(false);
      onLoading(false);
    }
  };

  const handleNearMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeoLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocation('Near Me (Current Location)');
          handleLocationSearch();
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please enter your postcode manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const renderFeaturedBusinesses = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-charcoal flex items-center gap-2">
          <Star className="h-5 w-5 text-amber-500" />
          Featured Businesses
          <Badge className="bg-amber-100 text-amber-800">Premium</Badge>
        </h3>
        <p className="text-sm text-gray-600">Priority visibility for premium members</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {searchResults.featured.map((business) => (
          <Card key={business.id} className="hover:shadow-lg transition-shadow border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <img 
                  src={business.logo} 
                  alt={business.name}
                  className="w-16 h-16 rounded-lg object-cover border-2 border-amber-200"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-charcoal truncate">{business.name}</h4>
                    {business.isVerified && (
                      <Verified className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {business.tier}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {business.address}
                  </p>
                  {business.offers.map((offer, index) => (
                    <Badge key={index} className="bg-green-100 text-green-800 text-xs mr-1 mb-2">
                      {offer}
                    </Badge>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <Button 
                      size="sm" 
                      className="flex-1 text-xs"
                      onClick={() => window.open(`https://${business.chatbotUrl}`, '_blank')}
                    >
                      <MessageCircle className="h-3 w-3 mr-1" />
                      View Deals
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs">
                      <QrCode className="h-3 w-3 mr-1" />
                      QR Code
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderVerifiedBusinesses = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-charcoal">Verified Shoppers Discount Club Retailers</h3>
        <Badge className="bg-primary/10 text-primary">Official Partners</Badge>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {searchResults.verified.map((business) => (
          <Card key={business.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <img 
                  src={business.logo} 
                  alt={business.name}
                  className="w-12 h-12 rounded object-cover"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm truncate">{business.name}</h4>
                  <p className="text-xs text-gray-600 truncate">{business.address}</p>
                  <Button size="sm" className="mt-1 text-xs w-full">
                    View Offers
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderJobsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-charcoal flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-blue-600" />
          Local Jobs in {location}
        </h3>
        <Button size="sm" variant="outline">Post Job</Button>
      </div>
      
      <div className="space-y-3">
        {searchResults.jobs.map((job) => (
          <Card key={job.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-charcoal">{job.title}</h4>
                  <p className="text-sm text-gray-600">{job.company} • {job.location}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">{job.type}</Badge>
                    <Badge className="bg-green-100 text-green-800 text-xs">{job.salary}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{job.posted}</p>
                  <Button size="sm" className="mt-2">Apply Now</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderEventsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-charcoal flex items-center gap-2">
          <Calendar className="h-5 w-5 text-purple-600" />
          What's On in {location}
        </h3>
        <Button size="sm" variant="outline">Add Event</Button>
      </div>
      
      <div className="space-y-3">
        {searchResults.events.map((event) => (
          <Card key={event.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-charcoal">{event.title}</h4>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {event.location}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {event.date} • {event.time}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className="bg-purple-100 text-purple-800 text-xs mb-2">{event.type}</Badge>
                  <Button size="sm" variant="outline">Learn More</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Live Offers Bar */}
      <div className="bg-gradient-to-r from-primary/10 to-orange/10 rounded-xl p-4 border border-primary/20">
        <h3 className="text-sm font-semibold text-charcoal mb-3 text-center">
          🎯 Live Offers Available Now
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {liveOffers.map((offer, index) => (
            <div key={index} className="text-center">
              <div className={`w-10 h-10 ${offer.color} rounded-full flex items-center justify-center mx-auto mb-1`}>
                <offer.icon className="h-5 w-5 text-white" />
              </div>
              <p className="text-xs font-medium text-gray-700">{offer.label}</p>
              <p className="text-xs font-bold text-primary">{offer.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Location Search */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="text-center flex items-center justify-center gap-2">
            <MapPinned className="h-5 w-5 text-primary" />
            Find Local Businesses & Offers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="flex-1">
              <Input
                placeholder="Enter postcode, town, or area (e.g., Birmingham, B1 2AA)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
                className="border-primary/20"
              />
            </div>
            <Button onClick={handleNearMe} variant="outline" className="px-3">
              <Navigation className="h-4 w-4" />
            </Button>
            <Button onClick={handleLocationSearch} disabled={loading || !location.trim()}>
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          {!locationSet && (
            <p className="text-sm text-gray-600 text-center">
              📍 Enter your location to see local businesses, jobs, and events near you
            </p>
          )}
        </CardContent>
      </Card>

      {/* Search Results */}
      {locationSet && (
        <div className="space-y-6">
          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="directory" className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                Directory
              </TabsTrigger>
              <TabsTrigger value="jobs" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Jobs
              </TabsTrigger>
              <TabsTrigger value="events" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                What's On
              </TabsTrigger>
              <TabsTrigger value="filters" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </TabsTrigger>
            </TabsList>

            <TabsContent value="directory" className="space-y-6">
              {renderFeaturedBusinesses()}
              {renderVerifiedBusinesses()}
            </TabsContent>

            <TabsContent value="jobs">
              {renderJobsTab()}
            </TabsContent>

            <TabsContent value="events">
              {renderEventsTab()}
            </TabsContent>

            <TabsContent value="filters">
              <Card>
                <CardHeader>
                  <CardTitle>Search Filters</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Discount Types</label>
                    <div className="grid grid-cols-2 gap-2">
                      {discountTypeFilters.map((type) => (
                        <label key={type} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filters.discountTypes.includes(type)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  discountTypes: [...filters.discountTypes, type]
                                });
                              } else {
                                setFilters({
                                  ...filters,
                                  discountTypes: filters.discountTypes.filter(t => t !== type)
                                });
                              }
                            }}
                          />
                          <span className="text-sm">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.verifiedOnly}
                        onChange={(e) => setFilters({...filters, verifiedOnly: e.target.checked})}
                      />
                      <span className="text-sm">Verified Partners Only</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default LocationFirstSearch;